import {
  GERMAN_STATE_CODES,
  GermanStateCode,
  germanStateCodeSchema,
} from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { GERMAN_STATE_LABEL } from '@/products/leave/features/leave-labels';
import {
  useLeavePolicyAdmin,
  useUpdateLeavePolicy,
} from '@/products/leave/features/use-leave-admin';

type PolicyDraft = {
  annualVacationDaysFullTime: string;
  referenceWorkDaysPerWeek: string;
  carryOverExpireMonth: string;
  carryOverExpireDay: string;
  maxCarryOverDays: string;
  defaultStateCode: string;
  requireSubstitute: boolean;
  enforceCapacity: boolean;
  allowPastRequests: boolean;
  approvalSteps: string;
  maxOvertimeHours: string;
  enforceOvertimeLimit: boolean;
  datevConsultantNumber: string;
  datevClientNumber: string;
  workingTimeMode: 'off' | 'warn' | 'enforce';
  hoursPerFullDay: string;
  reminderDaysBefore: string;
};

export function LeaveAdminPolicyPage() {
  usePageTitle('Urlaubsrichtlinie');
  const policy = useLeavePolicyAdmin();
  const save = useUpdateLeavePolicy();
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof PolicyDraft, string>>>({});

  const values: PolicyDraft = draft ?? {
    annualVacationDaysFullTime: String(policy.data?.annualVacationDaysFullTime ?? ''),
    referenceWorkDaysPerWeek: String(policy.data?.referenceWorkDaysPerWeek ?? ''),
    carryOverExpireMonth: String(policy.data?.carryOverExpireMonth ?? ''),
    carryOverExpireDay: String(policy.data?.carryOverExpireDay ?? ''),
    maxCarryOverDays:
      policy.data?.maxCarryOverDays == null ? '' : String(policy.data.maxCarryOverDays),
    defaultStateCode: policy.data?.defaultStateCode ?? GermanStateCode.NW,
    requireSubstitute: policy.data?.requireSubstitute ?? false,
    enforceCapacity: policy.data?.enforceCapacity ?? false,
    allowPastRequests: policy.data?.allowPastRequests ?? true,
    approvalSteps: String(policy.data?.approvalSteps ?? 1),
    maxOvertimeHours:
      policy.data?.maxOvertimeHours == null ? '' : String(policy.data.maxOvertimeHours),
    enforceOvertimeLimit: policy.data?.enforceOvertimeLimit ?? false,
    datevConsultantNumber: policy.data?.datevConsultantNumber ?? '',
    datevClientNumber: policy.data?.datevClientNumber ?? '',
    workingTimeMode: policy.data?.workingTimeMode ?? 'warn',
    hoursPerFullDay: String(policy.data?.hoursPerFullDay ?? ''),
    reminderDaysBefore: String(policy.data?.reminderDaysBefore ?? ''),
  };

  function patch(valuesPatch: Partial<PolicyDraft>) {
    setDraft((current) => ({ ...(current ?? values), ...valuesPatch }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof PolicyDraft, string>> = {};
    const annual = Number(values.annualVacationDaysFullTime);
    const reference = Number(values.referenceWorkDaysPerWeek);
    const hours = Number(values.hoursPerFullDay);
    const maxCarry = values.maxCarryOverDays.trim();
    const maxOvertime = values.maxOvertimeHours.trim();
    if (!Number.isFinite(annual) || annual < 0 || annual > 60) {
      next.annualVacationDaysFullTime = 'Bitte einen Anspruch zwischen 0 und 60 eingeben.';
    }
    if (!Number.isFinite(reference) || reference < 1 || reference > 7) {
      next.referenceWorkDaysPerWeek = 'Bitte 1 bis 7 Arbeitstage pro Woche eingeben.';
    }
    if (!Number.isFinite(hours) || hours < 0.5 || hours > 24) {
      next.hoursPerFullDay = 'Bitte 0,5 bis 24 Stunden pro Tag eingeben.';
    }
    if (maxCarry !== '') {
      const parsed = Number(maxCarry.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 60) {
        next.maxCarryOverDays = 'Leer = unbegrenzt, sonst 0 bis 60 Tage.';
      }
    }
    if (maxOvertime !== '') {
      const parsed = Number(maxOvertime.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 9999) {
        next.maxOvertimeHours = 'Leer = keine Grenze, sonst 0 bis 9999 Stunden.';
      }
    }
    if (
      values.datevConsultantNumber.trim() !== '' &&
      !/^\d{1,7}$/.test(values.datevConsultantNumber.trim())
    ) {
      next.datevConsultantNumber = 'Leer oder 1 bis 7 Ziffern.';
    }
    if (
      values.datevClientNumber.trim() !== '' &&
      !/^\d{1,5}$/.test(values.datevClientNumber.trim())
    ) {
      next.datevClientNumber = 'Leer oder 1 bis 5 Ziffern.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!validate()) {
      return;
    }
    const maxCarry = values.maxCarryOverDays.trim();
    const maxOvertime = values.maxOvertimeHours.trim();
    try {
      await save.mutateAsync({
        annualVacationDaysFullTime: Number(values.annualVacationDaysFullTime),
        referenceWorkDaysPerWeek: Number(values.referenceWorkDaysPerWeek),
        vacationYearStartMonth: 1,
        carryOverExpireMonth: Number(values.carryOverExpireMonth),
        carryOverExpireDay: Number(values.carryOverExpireDay),
        requireSubstitute: values.requireSubstitute,
        enforceCapacity: values.enforceCapacity,
        hoursPerFullDay: Number(values.hoursPerFullDay),
        reminderDaysBefore: Number(values.reminderDaysBefore),
        maxCarryOverDays: maxCarry === '' ? null : Number(maxCarry.replace(',', '.')),
        defaultStateCode: germanStateCodeSchema.parse(values.defaultStateCode),
        allowPastRequests: values.allowPastRequests,
        approvalSteps: values.approvalSteps === '2' ? 2 : 1,
        maxOvertimeHours: maxOvertime === '' ? null : Number(maxOvertime.replace(',', '.')),
        enforceOvertimeLimit: values.enforceOvertimeLimit,
        datevConsultantNumber: values.datevConsultantNumber.trim() || null,
        datevClientNumber: values.datevClientNumber.trim() || null,
        workingTimeMode: values.workingTimeMode,
      });
      setDraft(null);
      toast.success('Richtlinie gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Richtlinie konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Urlaubsrichtlinie"
        description="Anspruch, Verfall, Vertretungspflicht und Stunden pro Tag."
      />
      {policy.isPending ? (
        <p className="text-sm text-muted-foreground">Richtlinie wird geladen …</p>
      ) : null}
      <form
        className={`${leavePanelClassName} space-y-4`}
        onSubmit={(event) => void onSubmit(event)}
      >
        <p className="text-sm text-muted-foreground">Das Urlaubsjahr ist das Kalenderjahr.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="annual-vacation"
            label="Jahresanspruch (Vollzeit)"
            value={values.annualVacationDaysFullTime}
            error={errors.annualVacationDaysFullTime}
            onChange={(value) => patch({ annualVacationDaysFullTime: value })}
          />
          <Field
            id="reference-days"
            label="Referenz-Arbeitstage pro Woche"
            value={values.referenceWorkDaysPerWeek}
            error={errors.referenceWorkDaysPerWeek}
            onChange={(value) => patch({ referenceWorkDaysPerWeek: value })}
          />
          <Field
            id="hours-per-day"
            label="Stunden pro vollem Tag"
            value={values.hoursPerFullDay}
            error={errors.hoursPerFullDay}
            onChange={(value) => patch({ hoursPerFullDay: value })}
          />
          <Field
            id="carry-month"
            label="Übertrag verfällt im Monat"
            value={values.carryOverExpireMonth}
            onChange={(value) => patch({ carryOverExpireMonth: value })}
          />
          <Field
            id="carry-day"
            label="Übertrag verfällt am Tag"
            value={values.carryOverExpireDay}
            onChange={(value) => patch({ carryOverExpireDay: value })}
          />
          <Field
            id="max-carry"
            label="Übertrag höchstens (leer = unbegrenzt)"
            value={values.maxCarryOverDays}
            error={errors.maxCarryOverDays}
            onChange={(value) => patch({ maxCarryOverDays: value })}
          />
          <div>
            <Label htmlFor="default-state">Bundesland der Standardwoche</Label>
            <Select
              value={values.defaultStateCode}
              onValueChange={(value) => patch({ defaultStateCode: value })}
            >
              <SelectTrigger id="default-state" className="mt-2 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GERMAN_STATE_CODES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {GERMAN_STATE_LABEL[code]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field
            id="reminder-days"
            label="Erinnerung Tage vorher"
            value={values.reminderDaysBefore}
            onChange={(value) => patch({ reminderDaysBefore: value })}
          />
          <Field
            id="max-overtime"
            label="Überstunden höchstens (leer = keine Grenze)"
            value={values.maxOvertimeHours}
            error={errors.maxOvertimeHours}
            onChange={(value) => patch({ maxOvertimeHours: value })}
          />
          <Field
            id="datev-consultant"
            label="DATEV-Beraternummer (leer = nicht gesetzt)"
            value={values.datevConsultantNumber}
            error={errors.datevConsultantNumber}
            onChange={(value) => patch({ datevConsultantNumber: value })}
          />
          <Field
            id="datev-client"
            label="DATEV-Mandantennummer (leer = nicht gesetzt)"
            value={values.datevClientNumber}
            error={errors.datevClientNumber}
            onChange={(value) => patch({ datevClientNumber: value })}
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={values.requireSubstitute}
              onCheckedChange={(checked) => patch({ requireSubstitute: checked })}
            />
            Vertretung ist Pflicht
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={values.enforceCapacity}
              onCheckedChange={(checked) => patch({ enforceCapacity: checked })}
            />
            Besetzungswarnung blockiert Anträge
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={values.enforceOvertimeLimit}
              onCheckedChange={(checked) => patch({ enforceOvertimeLimit: checked })}
            />
            Überstundengrenze blockiert Buchungen
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={values.allowPastRequests}
              onCheckedChange={(checked) => patch({ allowPastRequests: checked })}
            />
            Anträge in der Vergangenheit erlauben
          </label>
        </div>
        <div className="max-w-xs">
          <Label htmlFor="approval-steps">Genehmigungsschritte</Label>
          <Select
            value={values.approvalSteps}
            onValueChange={(value) => patch({ approvalSteps: value })}
          >
            <SelectTrigger id="approval-steps" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Ein Schritt</SelectItem>
              <SelectItem value="2">Zwei Schritte</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="max-w-xs">
          <Label htmlFor="working-time-mode">Zeiterfassung</Label>
          <Select
            value={values.workingTimeMode}
            onValueChange={(value) =>
              patch({
                workingTimeMode: value as PolicyDraft['workingTimeMode'],
              })
            }
          >
            <SelectTrigger id="working-time-mode" className="mt-2 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Aus</SelectItem>
              <SelectItem value="warn">Warnen</SelectItem>
              <SelectItem value="enforce">Regeln erzwingen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={save.isPending}>
          Speichern
        </Button>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="mt-2"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? <p className="mt-1 text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
